import { FC, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Widget, WidgetSimpleList } from '@factorialco/f0-react/dist/experimental'
import {
  FinanceDtosPendingActionsOverviewSummaryScopeEnum,
  FinancePendingActionsDocument,
  FinancePendingActionsQuery,
  FinancePendingActionsQueryVariables,
} from 'generated/resources/graphql'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { EmptyState } from 'modules/finance/components/Widgets/common/EmptyState'
import { buildWidget } from 'modules/finance/components/Widgets/common/widgets'

const prepareData = (data: FinancePendingActionsQuery) => {
  const pendingActions = data.finance.pendingActionsSummariesConnection.nodes
  const { totalAlerts, criticalAlerts, warningAlerts } = pendingActions.reduce(
    (acc, { count, type }) => {
      acc.totalAlerts += count
      if (type === 'critical') acc.criticalAlerts += count
      if (type === 'warning') acc.warningAlerts += count
      return acc
    },
    { totalAlerts: 0, criticalAlerts: 0, warningAlerts: 0 }
  )

  const listItems = pendingActions.map((pendingAction) => {
    const item: {
      count: number
      id: string
      title: string
      alert?: {
        text: string
        level: 'critical' | 'warning'
      }
      resourceUrl: string
    } = {
      count: pendingAction.count,
      id: pendingAction.id,
      title: pendingAction.title,
      resourceUrl: pendingAction.resourceUrl || '',
    }

    if (pendingAction.type === 'critical') {
      item.alert = {
        text: i18n.t('finance.overview.pending_actions.overdue'),
        level: 'critical',
      }
    } else if (pendingAction.type === 'warning') {
      item.alert = {
        text: i18n.t('finance.overview.pending_actions.needs_attention'),
        level: 'warning',
      }
    }

    return item
  })

  const headerData = {
    title: i18n.t('finance.overview.pending_actions.title'),
    count: totalAlerts,
  }

  const summariesData = [
    {
      label: i18n.t('finance.overview.pending_actions.urgent_actions'),
      value: criticalAlerts,
    },
    {
      label: i18n.t('finance.overview.pending_actions.alerts'),
      value: warningAlerts,
    },
    {
      label: i18n.t('finance.overview.pending_actions.review_actions'),
      value: totalAlerts - criticalAlerts - warningAlerts,
    },
  ]

  const urlsByActionId = pendingActions.reduce(
    (acc: { [key: string]: string }, { id, resourceUrl }) => {
      if (resourceUrl != null) {
        acc[id] = resourceUrl
      }
      return acc
    },
    {}
  )

  return {
    urlsByActionId,
    headerData,
    summariesData,
    listItems,
    pendingActionsCount: pendingActions.length || 0,
    totalAlerts,
  }
}

const PendingActionsWidget: FC<{
  data: ReturnType<typeof prepareData>
}> = ({ data }) => {
  const navigate = useNavigate()
  const { headerData, summariesData, pendingActionsCount, listItems, urlsByActionId, totalAlerts } =
    data

  const onClickTask = (taskId: string | number) => {
    track(trackingEvents.accounting.overview.clickJtbd, { action_name_clicked: taskId })
    const url = urlsByActionId[taskId]
    if (url) {
      navigate(url)
    }
  }

  const [maxTasksToShow, setMaxTasksToShow] = useState<number>(7)

  useUnsafeEffect(() => {
    if (totalAlerts > 0) {
      const trackingData = {
        totalPendingActions: totalAlerts,
        ...listItems.reduce(
          (acc: { [key: string]: number }, item: { id: string; count: number }) => {
            acc[item.id] = item.count
            return acc
          },
          {}
        ),
      }
      track(trackingEvents.accounting.overview.pendingActionWidgetView, trackingData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Widget
      header={headerData}
      summaries={pendingActionsCount ? summariesData : undefined}
      action={
        maxTasksToShow < pendingActionsCount
          ? {
              label: i18n.t('finance.overview.pending_actions.see_all_actions', {
                count: pendingActionsCount - maxTasksToShow,
              }),
              onClick: () => setMaxTasksToShow(pendingActionsCount),
            }
          : undefined
      }
    >
      {!pendingActionsCount ? (
        <EmptyState
          icon="fireworks"
          textLine1={i18n.t('finance.overview.pending_actions.empty_state_line_1')}
          textLine2={i18n.t('finance.overview.pending_actions.empty_state_line_2')}
        />
      ) : (
        <WidgetSimpleList
          items={listItems.slice(0, maxTasksToShow)}
          onClickItem={(id) => onClickTask(id)}
          showAllItems
        />
      )}
    </Widget>
  )
}

export const buildPendingActionsWidget = (
  scopes: FinanceDtosPendingActionsOverviewSummaryScopeEnum[]
) => {
  return buildWidget<
    FinancePendingActionsQuery,
    ReturnType<typeof prepareData>,
    FinancePendingActionsQueryVariables
  >({
    id: 'pendingActionsWidget',
    component: PendingActionsWidget,
    document: FinancePendingActionsDocument,
    prepareData: (data: FinancePendingActionsQuery) => prepareData(data),
    queryArgs: {
      summaryScope: scopes,
    },
  })
}
